export type IntegrityEventType =
  | "tab_away"
  | "tab_return"
  | "fullscreen_exit"
  | "fullscreen_enter"
  | "blocked_copy"
  | "blocked_paste"
  | "blocked_contextmenu"
  | "shortcut_warning"
  | "snapshot_failed";

export type IntegrityEvent = {
  client_event_id: string;
  event_type: IntegrityEventType;
  occurred_at: string;
  returned_at?: string;
  duration_ms?: number;
  metadata: Record<string, string | number | boolean | null>;
};

export type PresenceState = {
  awaySince: number | null;
  awayClientEventId?: string | null;
};

export type PresenceTransition = {
  state: PresenceState;
  events: IntegrityEvent[];
  changed: boolean;
};

export function createUuid(): string {
  const browserCrypto =
    typeof crypto !== "undefined"
      ? (crypto as Crypto & { randomUUID?: () => string })
      : undefined;
  if (browserCrypto?.randomUUID) {
    return browserCrypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character) => {
      const random = (Math.random() * 16) | 0;
      const value = character === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    }
  );
}

export function transitionPresence(
  state: PresenceState,
  signal: "away" | "return",
  at: number,
  uuid: () => string = createUuid
): PresenceTransition {
  if (signal === "away") {
    if (state.awaySince !== null) {
      return { state, events: [], changed: false };
    }
    const clientEventId = uuid();
    return {
      state: { awaySince: at, awayClientEventId: clientEventId },
      events: [
        {
          client_event_id: clientEventId,
          event_type: "tab_away",
          occurred_at: new Date(at).toISOString(),
          metadata: { visibility_state: "hidden" },
        },
      ],
      changed: true,
    };
  }

  if (state.awaySince === null) {
    return { state, events: [], changed: false };
  }

  const returnedAtIso = new Date(Math.max(at, state.awaySince)).toISOString();

  return {
    state: { awaySince: null, awayClientEventId: null },
    changed: true,
    events: [
      {
        client_event_id: uuid(),
        event_type: "tab_return",
        occurred_at: returnedAtIso,
        metadata: { visibility_state: "visible" },
      },
    ],
  };
}

export function createIntegrityEvent(
  eventType: Exclude<IntegrityEventType, "tab_away" | "tab_return">,
  metadata: IntegrityEvent["metadata"] = {},
  at = Date.now()
): IntegrityEvent {
  return {
    client_event_id: createUuid(),
    event_type: eventType,
    occurred_at: new Date(at).toISOString(),
    metadata,
  };
}

export function createSnapshotFailureEvent(
  sourceClientEventId: string,
  occurredAt: string,
  reason: string
): IntegrityEvent {
  const first = Number.parseInt(sourceClientEventId.charAt(0), 16);
  const clientEventId = Number.isNaN(first)
    ? createUuid()
    : `${((first + 1) % 16).toString(16)}${sourceClientEventId.slice(1)}`;
  return {
    client_event_id: clientEventId,
    event_type: "snapshot_failed",
    occurred_at: occurredAt,
    metadata: { reason, snapshot_error: reason },
  };
}
